import React, {
  forwardRef,
  ReactNode,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { BOT_CHART_COMPARE_ID, BOT_CHART_COMPARISON_ID } from "testIds";
import styled, { css, useTheme } from "styled-components";
import { useSelector } from "react-redux";
import moment from "moment";
import { skipToken } from "@reduxjs/toolkit/query";
import { ArrowLeftRight, ChevronDown } from "lucide-react";
import {
  BotGraphType,
  BotStatus,
  BotType,
  FormattedChartDataPoint,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useGetBotsGraphsData,
  useMyBotsStatistics,
} from "@shared/features/bots/hooks";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import { getMyBotsStatisticsValues } from "@shared/features/bots/utils";
import { selectFilteredBots } from "@shared/slices/bots.slice";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { useIncludeArchived } from "features/filters/hooks";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import {
  formatSignedMoney,
  shownAt,
  splitFraction,
} from "features/portfolio/NewLook/format.ts";
import {
  BASE_CHART_TIME_RANGES,
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_MONTHS,
} from "@shared/common/constants";
import { DISPLAY_CURRENCIES } from "common/constants";
import { compactAxisValue } from "features/bots/NewLook/newLookBotUtils";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import { PhoneHeroRanges } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import ChartEmpty from "common/NewLook/ChartEmpty.tsx";
import useValueFlash from "common/NewLook/useValueFlash.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import NewLookProfitChart, {
  ProfitPoint,
} from "features/bots/NewLook/NewLookProfitChart";
import { formatDateLabel } from "@shared/utils/date";

interface NewLookBotsPerformanceProps {
  botType: BotType;
  isLoading?: boolean;
  /** Phone: the headline figures in one row; the chart opens on demand. */
  compact?: boolean;
}

type ChartKind = "accumulated" | "daily";

/** A formatted chart series as the profit chart's points (unix seconds). */
const toPoints = (data: FormattedChartDataPoint[]): ProfitPoint[] =>
  data
    .filter((point) => point.rawX !== undefined)
    .map((point) => ({ time: Number(point.rawX), value: point.y }));
const CHART_KINDS: readonly ChartKind[] = ["accumulated", "daily"];
const CHART_HEIGHT = 184;
const COMPACT_CHART_HEIGHT = 168;

/**
 * New look: the bots' results as the page's lead, built like the dashboard's
 * overview — the profit as the headline figure over its chart (the headline
 * follows the crosshair), the range's change under it, and beside it what
 * the bots are doing: how many trades they closed, how many run, and what the
 * open trades are worth now.
 */
const NewLookBotsPerformance = forwardRef(
  ({ botType, isLoading, compact }: NewLookBotsPerformanceProps, ref) => {
    const { t, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);
    const theme = useTheme();
    const tokens = getNewLookTokens(theme);
    const displayCurrency = useSelector(selectDisplayCurrency);
    const isUsd = displayCurrency === DISPLAY_CURRENCIES.USD;
    const symbol = isUsd ? "$" : "₿";
    const { getDisplayCurrencyAmountPrecision } = usePrecisions();
    const precision = getDisplayCurrencyAmountPrecision();
    const { isFetching, numClosedData, profitData } =
      useMyBotsStatistics(botType);
    const [chartKind, setChartKind] = useState<ChartKind>("accumulated");
    const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_1_MONTHS);
    const [expanded, setExpanded] = useState(false);
    const [compareCharts, setCompareCharts] = useState(false);
    const [hovered, setHovered] = useState<{
      kind: ChartKind;
      point: ProfitPoint;
    }>();
    const includeArchived = useIncludeArchived(botType);

    // The bots the figures count: the page's filters, not its search.
    const { getBotsDataArgs } = useContext(MyBotsPageContext)!;
    const botsSelector = useMemo(
      () =>
        getBotsDataArgs === skipToken
          ? () => undefined
          : selectFilteredBots(getBotsDataArgs),
      [getBotsDataArgs]
    );
    const filteredBots = useSelector(botsSelector);
    const profit = useMemo(
      () =>
        getMyBotsStatisticsValues(filteredBots, botType, displayCurrency)[1],
      [filteredBots, botType, displayCurrency]
    );
    const fleet = useMemo(() => {
      const bots = filteredBots || [];
      const changeIndex = isUsd ? 0 : 1;
      return {
        total: bots.length,
        active: bots.filter((bot) => bot.status === BotStatus.ACTIVE).length,
        openTrades: bots.reduce(
          (sum, bot) => sum + (bot.number_of_open_trades || 0),
          0
        ),
        openPnl: bots.reduce(
          (sum, bot) => sum + (bot.open_trades_change?.[changeIndex] || 0),
          0
        ),
      };
    }, [filteredBots, isUsd]);
    const openPnlFlash = useValueFlash(fleet.openPnl);

    const graphsArgs = useMemo(
      () => ({
        bot_type: botType,
        time_range: timeRange,
        include_archived: includeArchived,
      }),
      [botType, timeRange, includeArchived]
    );
    const graphData = useGetBotsGraphsData(graphsArgs);
    const chartLoading = graphData.status.isFetching || isLoading;
    useImperativeHandle(ref, () => ({ refetch: graphData.refetch }));

    const accumulated = useMemo(
      () => toPoints(graphData.data.accumulatedProfit),
      [graphData.data.accumulatedProfit]
    );
    const daily = useMemo(
      () => toPoints(graphData.data.profitByDay),
      [graphData.data.profitByDay]
    );
    const intraday = timeRange === TIME_RANGE_1_DAY;
    const rangeLabel = timeRange.toUpperCase();

    const signedMoney = (value: number) =>
      formatSignedMoney(value, symbol, precision);
    const formatAxisValue = (value: number, step: number) =>
      compactAxisValue(value, step, { prefix: symbol });
    const formatDay = (time: number) =>
      formatDateLabel(moment.unix(time), intraday ? "MMM D, HH:mm" : "MMM D");

    // Headline: the bots' profit; on the accumulated chart it follows the
    // crosshair. The line under it: what the range added (or, under the
    // crosshair, what the day made / what was added since the range began).
    const rangeStart = accumulated[0];
    const rangeEnd = accumulated[accumulated.length - 1];
    const rangeChange =
      rangeStart && rangeEnd ? rangeEnd.value - rangeStart.value : 0;
    const headline =
      hovered?.kind === "accumulated" ? hovered.point.value : profit;
    let changeValue = rangeChange;
    let changeMeta = t("performance.over_range", { range: rangeLabel });
    if (hovered?.kind === "accumulated" && rangeStart) {
      changeValue = hovered.point.value - rangeStart.value;
      changeMeta = t("performance.since", {
        time: formatDay(rangeStart.time),
      });
    } else if (hovered?.kind === "daily") {
      changeValue = hovered.point.value;
      changeMeta = t("performance.on_day", {
        time: formatDay(hovered.point.time),
      });
    }
    const hasHistory = accumulated.length > 1 || daily.length > 1;
    const figuresLoading = !!isLoading && !filteredBots?.length;

    const renderChart = (
      kind: ChartKind,
      height: number,
      edgeToEdge = false
    ) => {
      const points = kind === "accumulated" ? accumulated : daily;
      if (chartLoading && points.length < 2) {
        return <Skeleton width="100%" height={height} radius={8} />;
      }
      if (points.length < 2) {
        return (
          <ChartEmpty height={height}>{t("performance.no_history")}</ChartEmpty>
        );
      }
      return (
        <NewLookProfitChart
          points={points}
          height={height}
          mode={kind === "accumulated" ? "line" : "bars"}
          edgeToEdge={edgeToEdge}
          intraday={intraday}
          formatAxisValue={formatAxisValue}
          ariaLabel={t(
            kind === "accumulated"
              ? "performance.chart_label"
              : "performance.daily_chart_label",
            { range: rangeLabel }
          )}
          onActiveChange={(point) =>
            setHovered((current) => {
              if (point) return { kind, point };
              return current?.kind === kind ? undefined : current;
            })
          }
        />
      );
    };

    const chartKindLabel = (kind: ChartKind) =>
      t(
        BotGraphTypeToDisplay[
          kind === "accumulated"
            ? BotGraphType.ACCUMULATING_PROFIT
            : BotGraphType.PROFIT_BY_DAY
        ]
      );
    const chartKindSwitch = (
      <SegmentTabs<ChartKind>
        options={CHART_KINDS}
        value={chartKind}
        onChange={(kind) => {
          setHovered(undefined);
          setChartKind(kind);
        }}
        ariaLabel={t("performance.chart_type_label")}
        getLabel={chartKindLabel}
      />
    );
    const changeRange = (range: TimeRange) => {
      setHovered(undefined);
      setTimeRange(range);
    };
    const rangeSwitch = (
      <SegmentTabs<TimeRange>
        options={BASE_CHART_TIME_RANGES as TimeRange[]}
        value={timeRange}
        onChange={changeRange}
        ariaLabel={t("performance.range_label")}
      />
    );

    const [whole, fraction] = splitFraction(
      getLocaleStringWithPrecision(Math.abs(headline), precision)
    );
    const shownHeadline = shownAt(headline, precision);
    const headlineColor = directionColor(tokens, headline);
    const headlineFigure = figuresLoading ? (
      <Skeleton width={compact ? 120 : 200} height={compact ? 26 : 40} />
    ) : (
      <Display
        $compact={compact}
        style={{ color: headlineColor }}
        aria-live="polite"
      >
        {shownHeadline > 0 ? "+" : shownHeadline < 0 ? "−" : ""}
        <Currency $compact={compact}>{symbol}</Currency>
        {whole}
        {fraction}
      </Display>
    );
    const changeLine = hasHistory && (
      <ChangeLine>
        <ChangeValue style={{ color: directionColor(tokens, changeValue) }}>
          {signedMoney(changeValue)}
        </ChangeValue>
        <Meta>{changeMeta}</Meta>
      </ChangeLine>
    );

    const countLabel = numClosedData.title;
    const botsCount = (count: number) =>
      i18n.t("performance.across_bots", {
        ns: LanguageNameSpaces.BOTS,
        count,
      }) as string;

    if (compact) {
      return (
        <Panel flush={true} data-testid="my-bots-performance">
          <CompactHead
            id="my-bots-statistics"
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setExpanded(!expanded);
              }
            }}
          >
            <CompactFigure>
              <Label>{profitData.title}</Label>
              {headlineFigure}
            </CompactFigure>
            <CompactFigure>
              <Label>{countLabel}</Label>
              {figuresLoading ? (
                <Skeleton width={48} height={26} />
              ) : (
                <CompactValue>{numClosedData.value}</CompactValue>
              )}
            </CompactFigure>
            <Chevron $expanded={expanded} aria-hidden={true}>
              <ChevronDown size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
            </Chevron>
          </CompactHead>
          <ExpandCollapse expanded={expanded}>
            <CompactChart>
              {/* The chart type first, then the change line, the chart and
                  its ranges, as on the other phone charts. */}
              <CompactRow>{chartKindSwitch}</CompactRow>
              {changeLine}
              <EdgeToEdge>
                {renderChart(chartKind, COMPACT_CHART_HEIGHT, true)}
              </EdgeToEdge>
              <PhoneHeroRanges<TimeRange>
                options={BASE_CHART_TIME_RANGES as TimeRange[]}
                value={timeRange}
                onChange={changeRange}
                label={t("performance.range_label")}
                surface="panel"
              />
            </CompactChart>
          </ExpandCollapse>
        </Panel>
      );
    }

    const metric = (
      key: string,
      label: ReactNode,
      value: ReactNode,
      meta: ReactNode,
      extra?: ReactNode
    ) => (
      <Metric key={key}>
        <Label>{label}</Label>
        {figuresLoading ? (
          <Skeleton width={96} height={26} />
        ) : (
          <>
            {value}
            {extra}
            <Meta>{meta}</Meta>
          </>
        )}
      </Metric>
    );

    const activeShare = fleet.total ? fleet.active / fleet.total : 0;
    const stopped = fleet.total - fleet.active;

    return (
      <Panel
        flush={true}
        data-testid="my-bots-performance"
        className="my-bots-performance"
      >
        <Layout id="my-bots-statistics" aria-busy={isFetching || undefined}>
          <Main>
            <MainHeader>
              <HeadlineBlock>
                <Label>{profitData.title}</Label>
                {headlineFigure}
                {changeLine}
              </HeadlineBlock>
              <Controls>
                {!compareCharts && chartKindSwitch}
                {rangeSwitch}
                <CompareToggle
                  type="button"
                  title={t(
                    compareCharts
                      ? "labels.single_chart"
                      : "labels.compare_charts"
                  )}
                  data-testid={BOT_CHART_COMPARE_ID}
                  aria-pressed={compareCharts}
                  onClick={() => {
                    setHovered(undefined);
                    setCompareCharts(!compareCharts);
                  }}
                >
                  <ArrowLeftRight
                    size={14}
                    strokeWidth={NEW_LOOK_ICON_STROKE}
                    aria-hidden={true}
                  />
                  <span className="compare-label">
                    {t(
                      compareCharts
                        ? "labels.single_chart"
                        : "labels.compare_charts"
                    )}
                  </span>
                </CompareToggle>
              </Controls>
            </MainHeader>
            {compareCharts ? (
              <Comparison data-testid={BOT_CHART_COMPARISON_ID}>
                {CHART_KINDS.map((kind) => (
                  <div key={kind}>
                    <ChartCaption>{chartKindLabel(kind)}</ChartCaption>
                    {renderChart(kind, CHART_HEIGHT - 20)}
                  </div>
                ))}
              </Comparison>
            ) : (
              <ChartArea>{renderChart(chartKind, CHART_HEIGHT)}</ChartArea>
            )}
          </Main>
          <Side>
            {metric(
              "count",
              countLabel,
              <MetricValue>
                {getLocaleStringWithPrecision(Number(numClosedData.value), 0)}
              </MetricValue>,
              botsCount(fleet.total)
            )}
            {metric(
              "active",
              t("performance.active_bots"),
              <MetricValue>
                {fleet.active}
                <MetricTotal> / {fleet.total}</MetricTotal>
              </MetricValue>,
              stopped
                ? t("performance.stopped_count", { count: stopped })
                : t("performance.all_running"),
              <ShareTrack aria-hidden={true}>
                <ShareFill style={{ width: `${activeShare * 100}%` }} />
              </ShareTrack>
            )}
            {metric(
              "open",
              t("backtesting.statistics.unrealized_pnl"),
              <MetricValue
                className={openPnlFlash}
                style={{ color: directionColor(tokens, fleet.openPnl) }}
              >
                {signedMoney(fleet.openPnl)}
              </MetricValue>,
              fleet.openTrades
                ? (i18n.t("performance.in_open_trades", {
                    ns: LanguageNameSpaces.BOTS,
                    count: fleet.openTrades,
                  }) as string)
                : t("performance.no_open_trades")
            )}
          </Side>
        </Layout>
      </Panel>
    );
  }
);

export default React.memo(NewLookBotsPerformance);

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  container-type: inline-size;
  min-width: 0;
  padding: 20px 20px 16px 24px;
`;
// The figure and the controls share the top row in fixed columns, so the
// controls never move when the hovered value changes width. Narrow panels
// stack them instead.
const MainHeader = styled.div`
  display: grid;
  /* Both columns size to their content, so the figure can't run under the
     controls; the controls line up with the figure (below the label). */
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: start;
  gap: 12px 24px;
  > :nth-child(2) {
    margin-top: 26px;
  }
  @container (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
    > :nth-child(2) {
      margin-top: 0;
    }
  }
`;
const HeadlineBlock = styled.div`
  min-width: 0;
`;
const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  @container (max-width: 1000px) {
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-left: 0;
  }
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
`;
const Display = styled.div<{ $compact?: boolean }>`
  margin-top: 4px;
  ${(p) =>
    p.$compact
      ? css`
          ${newLookType.figureLarge};
          /* Phone: never above 18 inside a card. */
          font-size: 18px;
          line-height: 24px;
          margin-top: 2px;
        `
      : newLookType.display};
  white-space: nowrap;
`;
const Currency = styled.span<{ $compact?: boolean }>`
  margin-right: 1px;
  font-size: ${(p) => (p.$compact ? 16 : 26)}px;
  font-weight: 500;
`;
const ChangeLine = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;
const ChangeValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const ChartArea = styled.div`
  margin-top: 20px;
`;
const ChartCaption = styled.div`
  margin-bottom: 4px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Comparison = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-top: 16px;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
// The same height as a segmented track, quiet until pressed.
const CompareToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  /* Icon only when the panel is too narrow for the label beside the figure. */
  @container (max-width: 1240px) {
    padding: 0 9px;
    .compare-label {
      display: none;
    }
  }
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: color 120ms, background-color 120ms;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).ink};
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &[aria-pressed="true"] {
    color: ${(p) => getNewLookTokens(p.theme).ink};
    background: ${(p) => getNewLookTokens(p.theme).well};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;
const Side = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 24px 20px;
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-radius: 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px 0;
  @media (max-width: 1100px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 24px;
    border-left: none;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    border-radius: 0 0 ${NEW_LOOK_RADIUS.panel}px ${NEW_LOOK_RADIUS.panel}px;
  }
`;
const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  &:last-child {
    padding-bottom: 0;
  }
  @media (max-width: 1100px) {
    padding: 8px 0 0;
    & + & {
      border-top: none;
    }
  }
`;
const MetricValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  border-radius: 4px;
  width: fit-content;
`;
const MetricTotal = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
  font-weight: 500;
`;
const ShareTrack = styled.div`
  height: 4px;
  margin: 4px 0 2px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  overflow: hidden;
`;
const ShareFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).accent};
  transition: width 300ms ease;
`;
const CompactHead = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 32px;
  align-items: center;
  gap: 12px;
  padding: 12px 12px 12px 16px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
    border-radius: ${NEW_LOOK_RADIUS.panel}px;
  }
`;
const CompactFigure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const CompactValue = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Chevron = styled.span<{ $expanded: boolean }>`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  background: ${(p) => getNewLookTokens(p.theme).well};
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
`;
const CompactChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
// The phone chart bleeds to the panel's edges (out of CompactChart's padding).
const EdgeToEdge = styled.div`
  margin: 0 -16px;
`;
const CompactRow = styled.div`
  display: flex;
  min-width: 0;
  /* The segmented track stretches; its options share the width. */
  > * {
    flex: 1;
    min-width: 0;
  }
  > * > * {
    flex: 1;
  }
`;
